import { Divider } from '@7f-ui/components/divider/divider'
import { SimpleModal } from '@7f-ui/components/modal/simple-modal'
import { RichText } from '@7f-ui/components/rich-text/rich-text'
import { Check } from '@phosphor-icons/react'

import type { PropsWithChildren } from 'react'

import { sanitizeBackendHtml } from '@/lib/security/sanitize-html'

import type { AvailableRoom } from '../hooks/use-available-rooms'
import { AccommodationRoomSegments } from '../rooms/accommodation-room-segments'
import { AccommodationRoomFacilitiesFlags } from './accommodation-room-facilities-flags'

export type AccommodationRoomDetailsModalProps = {
  isOpen: boolean
  onClose: VoidFunction
  room: Pick<
    AvailableRoom,
    'segments' | 'facilityHighlights' | 'size' | 'bathrooms' | 'descriptionHtml' | 'facilityTypes'
  > | null
  className?: string
}

export function AccommodationRoomDetailsModal({
  isOpen,
  onClose,
  room,
  className,
}: AccommodationRoomDetailsModalProps) {
  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title="Zimmerdetails"
      size="medium"
      className={className}
    >
      {() => {
        if (!room) return null

        const facilities = room.facilityTypes.filter(facilityType => facilityType.id !== 8)

        return (
          <>
            <div className="grid gap-6">
              {room.facilityHighlights.length > 0 && (
                <AccommodationRoomFacilitiesFlags
                  facilities={room.facilityHighlights.slice(0, 12)}
                />
              )}

              <AccommodationRoomSegments
                segments={room.segments}
                bathrooms={room.bathrooms}
                size={room.size}
              />

              <RichText text={sanitizeBackendHtml(room.descriptionHtml)} />
            </div>

            {facilities.length > 0 && (
              <>
                <Divider className="my-8" />
                <ul className="divide-y divide-primary/15 *:py-8 *:first:pt-0 *:last:pb-0">
                  {facilities.map(facilityType => (
                    <li key={facilityType.id}>
                      <h2 className="mb-6 font-heading2">{facilityType.name}</h2>
                      <ul className="flex flex-col gap-4">
                        {facilityType.facilities.map(facility => (
                          <FacilityLineItem key={facility.id}>{facility.name}</FacilityLineItem>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )
      }}
    </SimpleModal>
  )
}

function FacilityLineItem({ children }: PropsWithChildren) {
  return (
    <li className="flex items-center gap-6">
      <Check size={24} />
      {children}
    </li>
  )
}
